import { AnalyticsEventName } from '@dupe/common/constants'
import { FeatureFlags, UploadFileTypes } from '@dupe/common/constants'
import { MutableRefObject, useState } from 'react'
import { useSearchContext } from '~/src/context/SearchProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useFetchUserResultCounts from '~/src/hooks/useFetchUserResultCounts'
import useHasProSubscription from '~/src/hooks/useHasProSubscription'
import useInIframe from '~/src/hooks/useInIframe'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import logger from '~/src/lib/logger'

const IMAGE_RESULTS_THRESHOLD = 10

const useInputImageUpload = (
  filePickerRef: MutableRefObject<HTMLInputElement | null>
) => {
  const analytics = useAnalytics()
  const [showPaywall, setShowPaywall] = useState(false)
  const { setUploadClickType, setSourceType } = useSearchContext()

  const { inIframe } = useInIframe()

  const { data: userResultCounts } = useFetchUserResultCounts()
  const totalImageResults = userResultCounts?.totalImageResults || 0

  const hasProSubscription = useHasProSubscription()

  const { featureOn: paywallDrawerFeatureEnabled } = useIsFeatureOn(
    FeatureFlags.PaywallDrawer
  )

  const { featureOn: restyleRoomFeatureEnabled } = useIsFeatureOn(
    FeatureFlags.RestyleRoom
  )

  const openImagePicker = ({
    source = 'dupe-input-camera',
    callback,
  }: {
    source?: 'dupe-input-camera' | 'dupe-input-upload' | 'dupe-input-restyle'
    callback?: () => void
  }) => {
    setSourceType(source)

    const triggerFilePicker = () => {
      setUploadClickType(UploadFileTypes.IMAGE)

      if (filePickerRef.current) {
        filePickerRef.current.click()

        if (callback) {
          callback()
        }
      }
    }

    if (inIframe) {
      window.open(`/?camera=1`)
    } else {
      logger.debug('[openImagePicker] callback', { source })

      // Trigger callback to move into SearchProvider - Dropzone
      // This has it's own auth check - but will keep the context of the selected files
      if (!paywallDrawerFeatureEnabled) {
        triggerFilePicker()
        return
      }

      if (hasProSubscription) {
        triggerFilePicker()
      } else {
        if (totalImageResults > IMAGE_RESULTS_THRESHOLD) {
          setShowPaywall(true)
        } else {
          triggerFilePicker()
        }
      }
    }
  }

  const onCameraPress = () => {
    if (restyleRoomFeatureEnabled && filePickerRef.current) {
      filePickerRef.current.capture = 'environment'
    }

    analytics.track(AnalyticsEventName.ClickedInputMedia, {
      media: 'camera',
    })

    openImagePicker({
      source: 'dupe-input-camera',
    })
  }

  const onUploadImage = () => {
    if (restyleRoomFeatureEnabled && filePickerRef.current) {
      filePickerRef.current.removeAttribute('capture')
    }

    analytics.track(AnalyticsEventName.ClickedInputMedia, {
      media: 'file-picker',
    })

    openImagePicker({
      source: 'dupe-input-upload',
    })
  }

  return {
    showPaywall,
    setShowPaywall,
    openImagePicker,
    onCameraPress,
    onUploadImage,
  }
}

export default useInputImageUpload
